/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-28 16:03:18
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Icon, TextStyle, Link, Badge } from '@shopify/polaris';
import { LockMinor, ExternalSmallMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import UnlockStar from 'src/assets/images/common/unlock-star.png';

import ActionsGroupOnListing from 'src/components/views/synonym/actions-group-on-listing';
import ImportModal from './import';
import ExportModal from './export';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import TypeFormEmbeded from 'src/components/views/common/typeform-embedded';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import TitleHelp from 'src/components/views/common/title-help';
import DidYouKnow from 'src/components/views/synonym/did-you-know';
import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';
import ProcessAddonV4 from 'src/components/views/subscription-v4/addon/process-addon';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';
import history from 'src/routes/history';

import * as synonymActions from 'src/stores/actions/synonym-actions';
import ManualSynonymSetting from 'src/components/views/synonym/manual-synonym-setting';
import { NUMBER_SYNONYMS_PER_PAGE } from 'src/constants/synonym';
import { getAISearchStatus } from 'src/helpers/search-settings-helper';
import { routes } from 'src/routes';
import {
  ADDONS_INFO,
  ADDONS_INFO_V4,
  AI_POWERED_SEARCH_NAME,
  SUBSCRIPTION_STATE
} from 'src/constants/subscription';
import { getAddonStatus } from 'src/stores/actions/subscription-v3-actions';
import LockImage from 'src/assets/images/lock-unlock-image/manual-synonym-lock.svg';

type Props = {
  getSynonyms: (q: string, skip: number, limit: number) => void,
  updateSynonyms: (synonyms: Array<Object>, actionName: string) => void,
  synonyms: Array<Object>,
  total: number,
  loading: boolean,
  success: boolean,
  updateSuccess: boolean,
  successFlag: number,
  saving: boolean,
  importing: boolean,
  scheduleSyncIn: number,
  waitSyncAt: number,
  selectedPlan: string,
  enableAISearch: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeImportModal: boolean,
  activeExportModal: boolean,
  skip: number,
  limit: number,
  enabled: Object,
  fromSynonymPageState: number,
  scheduleSyncIn: number,
  youtubeModal: boolean,
  modalAddon: boolean,
  changePlanFromReviewIOModal: boolean
};

class ManualSynonym extends Component<ComposedProps, State> {
  timerTyping = undefined;

  state = {
    activeImportModal: false,
    activeExportModal: false,
    skip: 0,
    limit: NUMBER_SYNONYMS_PER_PAGE,
    enabled: {},
    fromSynonymPageState: 0,
    scheduleSyncIn: 0,
    youtubeModal: false,
    filter: {},
    modalAddon: false,
    changePlanFromReviewIOModal: false
  };

  render() {
    const {
      i18n,
      synonyms,
      enabledSubscriptionV4,
      updateSuccess,
      enabledFeature,
      selectedPlan,
      enableAISearch,
      aiSearchStatus,
      useBoltOn
    } = this.props;
    const { skip, limit, fromSynonymPageState, youtubeModal, modalAddon, filter } = this.state;

    const isSubscribeNow = aiSearchStatus !== SUBSCRIPTION_STATE.NOT_TRIAL;

    // Check having synonym
    const havingSynonyms = !_.isEmpty(synonyms);

    const docUrl = URL.Link_V1_Search_Synonym_ManualGuide;

    const isEmptyFilter = (filter.search || filter.status) && !havingSynonyms;
    return (
      <div className="BC-Page BC-Page__Synonym">
        <Page
          title={i18n.translate('AdminHTML.Synonym.Synonym.manualTab')}
          primaryAction={
            (havingSynonyms || isEmptyFilter) && (
              <div className="BC-Page BC-Page__Button">
                <ActionsGroupOnListing skip={skip} limit={limit} hideEdit={isEmptyFilter} />
              </div>
            )
          }>
          <TitleHelp
            i18n={i18n}
            hrefUserManual={docUrl}
            onAnalytics={
              enabledFeature ? () => history.push(routes.analytics.overviewSearch()) : null
            }
            onImport={enabledFeature ? this.handleChangeImportModal : null}
            onExport={enabledFeature ? this.handleChangeExportModal : null}
          />

          {!enabledFeature ? (
            this.renderLockFeature(i18n)
          ) : (
            <Layout>
              <Layout.Section>
                <div className="BC-Container__Notifications">
                  {/* Did you know */}
                  <DidYouKnow />
                  {/* Processing update banner */}
                </div>
              </Layout.Section>

              {enableAISearch && (
                <Layout.Section>
                  <div className="manual-synonym-suggest--ai">
                    <div className="manual-synonym-suggest--ai-title">
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.AdvanceSearch.Tabs.aiSynonym')}
                      </TextStyle>
                      <Badge
                        progress="complete"
                        status={`${isSubscribeNow ? 'success' : 'warning'}`}>
                        {i18n.translate(
                          `AdminHTML.Common.Common.${isSubscribeNow ? 'status_On' : 'status_Trial'}`
                        )}
                      </Badge>
                    </div>
                    <Link
                      onClick={() => {
                        history.push(routes.aiSynonym.view());
                      }}>
                      {i18n.translate('AdminHTML.Synonym.Actions.goToAISynonym')}
                    </Link>
                  </div>
                </Layout.Section>
              )}

              {/* List of synonym */}
              <Layout.Section>
                <ManualSynonymSetting setSynonymFilter={filter => this.setState({ filter })} />
              </Layout.Section>
            </Layout>
          )}

          {!enableAISearch && enabledFeature && (
            <div
              className="manual-synonym-banner--unlock"
              onClick={() => {
                if (useBoltOn) this.setState({ changePlanFromReviewIOModal: true });
                else this.setState({ modalAddon: true });
              }}>
              <div style={{ fontSize: '16px' }}>
                <img width={16} height={16} src={UnlockStar} alt="" />{' '}
                {i18n.translate('AdminHTML.Synonym.Synonym.manual_UnlockContent', {
                  t: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.Synonym.Synonym.manual_UnlockContent_t')}
                    </TextStyle>
                  )
                })}
              </div>
              <div className="unlock-button Polaris-Button">
                <Icon source={LockMinor} />
                {i18n.translate(`AdminHTML.Common.Action.unLock`)}
              </div>
            </div>
          )}
        </Page>

        <ImportModal
          active={this.state.activeImportModal}
          handleChange={this.handleChangeImportModal}
          getSynonymsFirstPage={this.getSynonymsFirstPage}
          history={history}
        />

        <ExportModal
          active={this.state.activeExportModal}
          handleChange={this.handleChangeExportModal}
        />

        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbeded
            type="synonym"
            conditionShow={updateSuccess}
            conditionShowStateChange={fromSynonymPageState}
          />
        )}
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['SynonymsTut']}
          title={i18n.translate('AdminHTML.Synonym.Synonym.youtubeModal_Title')}
          onClose={this.handleCloseYoutubeModal}
        />

        {enabledSubscriptionV4 ? (
          <ProcessAddonV4
            openModalReview={modalAddon}
            addon={ADDONS_INFO_V4.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
            hadSubscribed={isSubscribeNow}
            handleAfterSubscribeAddon={() => getAddonStatus(AI_POWERED_SEARCH_NAME)}
            handleReviewModal={() => this.setState({ modalAddon: !modalAddon })}
          />
        ) : (
          <ProcessAddon
            openModalReview={modalAddon}
            addon={ADDONS_INFO.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
            hadSubscribed={isSubscribeNow}
            handleAfterSubscribeAddon={() => getAddonStatus(AI_POWERED_SEARCH_NAME)}
            handleReviewModal={() => this.setState({ modalAddon: !modalAddon })}
          />
        )}
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </div>
    );
  }

  renderLockFeature = i18n => {
    return (
      <div className="pfs-feature-lock-card">
        <img src={LockImage} alt="in-collection-search-lock" />
        <div className="content">{i18n.translate(`AdminHTML.Synonym.Plan.lockCard_Content01`)}</div>
        <p>{i18n.translate(`AdminHTML.Synonym.Plan.lockCard_Content02`)}</p>
        <div className="action">
          <div
            className={`Polaris-Button`}
            onClick={() =>
              this.props.useBoltOn
                ? this.setState({ changePlanFromReviewIOModal: true })
                : history.push(routes.subscription.package())
            }>
            <Icon source={LockMinor} />
            {i18n.translate(`AdminHTML.Common.Action.upgradeToUnlock`)}
          </div>
          <Link external url={URL['ManualSynonymGuide']}>
            {i18n.translate('AdminHTML.Common.Action.learnMore')}
            <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
          </Link>
        </div>
      </div>
    );
  };

  // Hide/show import modal
  handleChangeImportModal = () => {
    this.setState(({ activeImportModal }) => ({
      activeImportModal: !activeImportModal
    }));
  };

  // Hide/show export modal
  handleChangeExportModal = () => {
    this.setState(({ activeExportModal }) => ({
      activeExportModal: !activeExportModal
    }));
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSynonyms: (q, skip, limit) =>
    dispatch(synonymActions.getSynonyms({ q, skip, limit }, ownProps.i18n)),
  updateSynonyms: (synonyms, actionName) =>
    dispatch(synonymActions.updateSynonyms(synonyms, actionName, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const enableAISearch = _.get(
    state.oauth.tenantApp,
    'settings.instantSearch.settings.AI-synonyms',
    false
  );
  const statusStore = _.get(state.oauth, 'subscription.status', '');
  const addonStatus = _.get(state.subscriptionV3, `addonStatus.${AI_POWERED_SEARCH_NAME}`, {});
  const aiSearchStatus = getAISearchStatus(enableAISearch, statusStore, addonStatus);

  return {
    synonyms: _.get(state.synonym, 'synonyms', [{}, {}, {}]),
    total: _.get(state.synonym, 'total', 0),
    loading: _.get(state.synonym, 'loading', false),
    success: _.get(state.synonym, 'success', false),
    updateSuccess: _.get(state.synonym, 'updateSuccess', false),
    successFlag: _.get(state.synonym, 'successFlag', 0),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    saving: _.get(state.synonym, 'saving', false),
    importing: _.get(state.synonym, 'importing', false),
    scheduleSyncIn: _.get(state.synonym, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.synonym, 'waitSyncAt', 0),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    enableAISearch,
    enabledFeature: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.manual-synonyms',
      true
    ),
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),

    aiSearchStatus
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(ManualSynonym)),
  'SyncSynonym'
);
